import React from 'react'
// Consumables Filter Boxes Component
// Small filter boxes with colored Font Awesome icons for PPE, TOOLS, TYRES, OTHER categories

// Color scheme for consumables categories
const consumablesColors = {
    'ppe': { bg: 'bg-blue-100', icon: 'text-blue-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'boots': { bg: 'bg-amber-100', icon: 'text-amber-700', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'gloves': { bg: 'bg-red-100', icon: 'text-red-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'jackets': { bg: 'bg-yellow-100', icon: 'text-yellow-700', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'pants': { bg: 'bg-green-100', icon: 'text-green-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'socks': { bg: 'bg-pink-100', icon: 'text-pink-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'tools': { bg: 'bg-orange-100', icon: 'text-orange-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'tyres': { bg: 'bg-gray-100', icon: 'text-gray-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' },
    'other': { bg: 'bg-purple-100', icon: 'text-purple-600', activeBg: 'bg-blue-50', activeBorder: 'border-blue-500', activeIcon: 'text-blue-700' }
};

// Font Awesome icon map for consumables categories
const consumablesIconMap = {
    'ppe': 'fas fa-hard-hat',
    'boots': 'fas fa-shoe-prints',
    'gloves': 'fas fa-mitten',
    'jackets': 'fas fa-tshirt',
    'pants': 'fas fa-user',
    'socks': 'fas fa-socks',
    'head-protection': 'fas fa-hard-hat',
    'tools': 'fas fa-tools',
    'tyres': 'fas fa-circle',
    'other': 'fas fa-plus-circle'
};

export default function ConsumablesFilterBoxes({ selectedCategory, onCategorySelect }) {
    const [expandedCategory, setExpandedCategory] = React.useState(null);

    const mainCategories = [
        { slug: 'ppe', name: 'PPE', icon: consumablesIconMap.ppe, color: consumablesColors.ppe },
        { slug: 'tools', name: 'Tools', icon: consumablesIconMap.tools, color: consumablesColors.tools },
        { slug: 'tyres', name: 'Tyres', icon: consumablesIconMap.tyres, color: consumablesColors.tyres },
        { slug: 'consumables-other', name: 'Other', icon: consumablesIconMap.other, color: consumablesColors.other }
    ];

    const ppeSubcategories = [
        { slug: 'boots', name: 'Boots', icon: consumablesIconMap.boots, color: consumablesColors.boots },
        { slug: 'gloves', name: 'Gloves', icon: consumablesIconMap.gloves, color: consumablesColors.gloves },
        { slug: 'jackets', name: 'Jackets', icon: consumablesIconMap.jackets, color: consumablesColors.jackets },
        { slug: 'pants', name: 'Pants', icon: consumablesIconMap.pants, color: consumablesColors.pants },
        { slug: 'socks', name: 'Socks', icon: consumablesIconMap.socks, color: consumablesColors.socks },
        { slug: 'head-protection', name: 'Head Protection', icon: consumablesIconMap['head-protection'], color: consumablesColors.boots }
    ];

    const handleCategoryClick = (slug) => {
        if (slug === 'ppe') {
            setExpandedCategory(expandedCategory === 'ppe' ? null : 'ppe');
        } else {
            onCategorySelect(slug);
        }
    };

    const handleSubcategoryClick = (slug) => {
        onCategorySelect(slug);
    };

    const isActive = (slug) => selectedCategory === slug;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <span className="text-xs font-black text-gray-900 uppercase tracking-widest">
                    Consumables Categories
                </span>
            </div>
            
            {/* Main Category Filter Boxes */}
            <div className="grid grid-cols-4 gap-3 mb-3">
                {mainCategories.map(category => (
                    <button
                        key={category.slug}
                        onClick={() => handleCategoryClick(category.slug)}
                        className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 ${
                            isActive(category.slug) 
                                ? `${category.color.activeBorder} ${category.color.activeBg} shadow-md scale-105` 
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                        }`}
                    >
                        <div className={`w-12 h-12 mb-2 flex items-center justify-center rounded-full ${
                            isActive(category.slug) ? category.color.activeBg : category.color.bg
                        }`}>
                            <i className={`${category.icon} text-2xl ${
                                isActive(category.slug) ? category.color.activeIcon : category.color.icon
                            }`}></i>
                        </div>
                        <span className={`text-xs font-bold text-center ${
                            isActive(category.slug) ? category.color.activeIcon : 'text-gray-700'
                        }`}>
                            {category.name}
                        </span>
                        {category.slug === 'ppe' && (
                            <span className="text-[10px] text-gray-500 mt-0.5">
                                {expandedCategory === 'ppe' ? '▲' : '▼'}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* PPE Subcategories - Expandable */}
            {expandedCategory === 'ppe' && (
                <div className="ml-4 animate-fade-in">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">
                            PPE Types
                        </span>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                        {ppeSubcategories.map(sub => (
                            <button
                                key={sub.slug}
                                onClick={() => handleSubcategoryClick(sub.slug)}
                                className={`flex flex-col items-center p-2 rounded-lg border transition-all duration-200 ${
                                    isActive(sub.slug) 
                                        ? `${sub.color.activeBorder} ${sub.color.activeBg} shadow-sm` 
                                        : 'border-gray-200 bg-white hover:border-blue-300'
                                }`}
                            >
                                <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                                    isActive(sub.slug) ? sub.color.activeBg : sub.color.bg
                                }`}>
                                    <i className={`${sub.icon} text-lg ${
                                        isActive(sub.slug) ? sub.color.activeIcon : sub.color.icon
                                    }`}></i>
                                </div>
                                <span className={`text-[10px] font-bold text-center mt-1 ${
                                    isActive(sub.slug) ? sub.color.activeIcon : 'text-gray-600'
                                }`}>
                                    {sub.name}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
